/**
 * Video CTA Configuration
 * Channel Invite (Telegram Groups) - principal. Config desde telegramGroupsVideo.
 */

import type { StaticPageVideoPage } from "./staticPageVideoConfig";
import {
  TELEGRAM_GROUPS_PLAYBACK_ID,
  TELEGRAM_GROUPS_CTA_URL,
  getTelegramGroupsVideoConfig,
  hasChannelInviteVideo,
} from "@/config/telegramGroupsVideo";
import {
  getMejoresAmigosPlaybackIdByCountry,
  getMejoresAmigosCTAUrlByCountry,
  MEJORES_AMIGOS_PLAYBACK_ID,
} from "@/config/mejoresAmigosVideo";
import { formatNeighborhoodBetaLabel } from "@/../api/lib/neighborhoodBetaCatalog";

export type VideoCTAService =
  | "telegram"
  | "channel_invite"
  | "telegram_groups"
  | "mejores_amigos"
  | "custom";

export type MejoresAmigosTierKey = "medium" | "high";

export interface NeighborhoodVideoCtaContext {
  country?: string | null;
  province?: string | null;
  city?: string | null;
  neighborhood?: string | null;
  neighborhoodCity?: string | null;
  showBestFriends?: boolean;
  showBirthday?: boolean;
  ctaUrl?: string | null;
  ctaLabel?: string | null;
  mejoresAmigosTiers?: Partial<
    Record<MejoresAmigosTierKey, { url: string; label: string }>
  >;
}

export type SupportedLanguage = "en" | "es" | "de" | "fr" | "it" | "pt" | "nl" | "tr" | "ar" | "pl" | "ja" | "ko" | "id";

/** Resolución admin + env (`resolveMuxFriendlyFunnelOpts`). */
export type MuxFriendlyFunnelOpts = {
  friendlyBanner: "on" | "off" | "unset";
  freeJoinCopy: boolean;
};

/** One Vercel flag: CTA copy tuned for “join the free Telegram” funnel. */
export function readMuxCtaFreeJoinCopyEnvActive(): boolean {
  const raw = (import.meta.env.VITE_MUX_CTA_FREE_JOIN_COPY as string | undefined)
    ?.toLowerCase()
    .trim();
  return raw === "true" || raw === "1" || raw === "yes" || raw === "on";
}

function isMuxCtaFreeJoinCopyActive(funnel?: MuxFriendlyFunnelOpts | null): boolean {
  return funnel?.freeJoinCopy ?? readMuxCtaFreeJoinCopyEnvActive();
}

/** Líneas del cartel “no es VIP” (Mux CTA / overlay Mi VIP). */
const MUX_FRIENDLY_BANNER_FREE: Record<SupportedLanguage, string> = {
  es: "Podés entrar al canal gratis, con todo el cariño. No es el VIP de pago — un toque y listo.",
  en: "You can join the free channel — warmly, no pressure. Not paid VIP — one tap and you’re in.",
  de: "Du kannst dem Gratis-Kanal beitreten — ganz entspannt. Nicht der bezahlte VIP — ein Tipp genügt.",
  fr: "Tu peux rejoindre le canal gratuit, en douceur. Ce n’est pas le VIP payant — un tap et c’est parti.",
  it: "Puoi entrare nel canale gratis, con calore. Non è il VIP a pagamento — un tocco e ci sei.",
  pt: "Você pode entrar no canal grátis, com carinho. Não é o VIP pago — um toque e pronto.",
  nl: "Je mag gratis bij de groep — zonder stress. Geen betaalde VIP — één tik en je bent binnen.",
  tr: "Ücretsiz kanala katılabilirsin — sıcaklıkla, baskı yok. Ücretli VIP değil — bir dokunuş yeter.",
  ar: "يمكنك الانضمام إلى القناة المجانية — بدفء وبدون ضغط. ليس VIP مدفوعًا — لمسة واحدة وأنت داخل.",
  pl: "Możesz dołączyć do bezpłatnego kanału — ciepło, bez presji. Nie płatny VIP — jedno dotknięcie i gotowe.",
  ja: "無料チャンネルに参加できます — 温かく、プレッシャーなし。有料VIPではありません — ワンタップで完了。",
  ko: "무료 채널에 참여할 수 있습니다 — 따뜻하게, 부담 없이. 유료 VIP가 아닙니다 — 한 번 탭하면 됩니다.",
  id: "Kamu bisa bergabung ke saluran gratis — dengan hangat, tanpa tekanan. Bukan VIP berbayar — satu ketukan dan masuk.",
};

const MUX_FRIENDLY_BANNER_SOFT: Record<SupportedLanguage, string> = {
  es: "Esto es el espacio de la comunidad (gratis), no el plan VIP pago. El siguiente paso está abajo, con buena onda.",
  en: "This is the free community space — not paid VIP. Next step is below, nice and easy.",
  de: "Das ist der kostenlose Community-Bereich — nicht der bezahlte VIP. Der nächste Schritt steht unten, ganz relaxed.",
  fr: "Ici c’est l’espace communautaire gratuit — pas le VIP payant. L’étape suivante est en bas, tout en douceur.",
  it: "Qui c’è lo spazio community gratuito — non il VIP a pagamento. Il passo dopo è sotto, con calma.",
  pt: "Aqui é o espaço da comunidade grátis — não o VIP pago. O próximo passo está abaixo, com leveza.",
  nl: "Dit is de gratis community — niet de betaalde VIP. De volgende stap staat hieronder, rustig.",
  tr: "Bu ücretsiz topluluk alanı — ücretli VIP değil. Sonraki adım aşağıda, rahat bir şekilde.",
  ar: "هذا هو الفضاء المجتمعي المجاني — وليس VIP المدفوع. الخطوة التالية في الأسفل، بكل سهولة.",
  pl: "To jest bezpłatna przestrzeń społeczności — nie płatny VIP. Następny krok poniżej, spokojnie.",
  ja: "これは無料のコミュニティスペースです — 有料VIPではありません。次のステップは下にあります、気楽に。",
  ko: "이것은 무료 커뮤니티 공간입니다 — 유료 VIP가 아닙니다. 다음 단계는 아래에 있습니다, 편안하게.",
  id: "Ini adalah ruang komunitas gratis — bukan VIP berbayar. Langkah selanjutnya ada di bawah, santai saja.",
};

export function readMuxCtaFriendlyBannerEnv(): "on" | "off" | "unset" {
  const raw = (import.meta.env.VITE_MUX_CTA_FRIENDLY_BANNER as string | undefined)
    ?.toLowerCase()
    .trim();
  if (raw === "true" || raw === "1" || raw === "yes" || raw === "on") return "on";
  if (raw === "false" || raw === "0" || raw === "no" || raw === "off")
    return "off";
  return "unset";
}

/**
 * `VITE_MUX_CTA_FRIENDLY_BANNER=false` (u off/0/no) — bloquea cartel + funnel auto por URL en premium simple.
 * Si pasás `funnel` (admin + env ya resueltos), usa ese estado.
 */
export function isMuxCtaFriendlyBannerEnvStrictOff(
  funnel?: MuxFriendlyFunnelOpts | null,
): boolean {
  const b = funnel?.friendlyBanner ?? readMuxCtaFriendlyBannerEnv();
  return b === "off";
}

/**
 * Texto breve encima del CTA en `MuxVideoPlayerWithCTA` (tono suave).
 * Deja explícito que **no es el plan VIP** (acceso canal / comunidad / grupo).
 * Incluye página estática `grupo-privado` aunque no venga `service`.
 *
 * **Cuándo se muestra:** con `VITE_MUX_CTA_FRIENDLY_BANNER=true` (o 1/on/yes), **o** si
 * `VITE_MUX_CTA_FREE_JOIN_COPY` está activo (mensaje sin cargo). Si no ponés ninguna de
 * las dos, no hay cartel. Con `FRIENDLY_BANNER=false` se oculta el cartel **salvo** que
 * `FREE_JOIN_COPY` esté activo (sigue el aviso sin cargo).
 * En overlay Mi VIP (`premiumSimpleMembershipFreeFunnel`) el cartel puede mostrarse aunque
 * `FRIENDLY_BANNER` no esté en `on`, salvo `FRIENDLY_BANNER=false` explícito sin `FREE_JOIN_COPY`.
 * Si el padre pasa `ctaSubtitle` y no es `premiumSimpleMembershipFreeFunnel`, no se muestra (evita duplicar).
 */
export function getMuxCtaFriendlyBannerMessage(
  lang: SupportedLanguage,
  ctx: {
    service?: VideoCTAService;
    ctaSubtitle?: string;
    staticPage?: StaticPageVideoPage | null;
    /**
     * Overlay “Mi VIP” en BioTemplatePremiumSimple cuando el CTA abre Telegram gratis:
     * muestra el aviso “no es VIP” sin exigir `service`/`staticPage` ni tapar el cartel con `ctaSubtitle`.
     */
    premiumSimpleMembershipFreeFunnel?: boolean;
  },
  funnel?: MuxFriendlyFunnelOpts | null,
): string | null {
  if (ctx.ctaSubtitle?.trim() && !ctx.premiumSimpleMembershipFreeFunnel)
    return null;

  if (ctx.premiumSimpleMembershipFreeFunnel) {
    const freeJoin = isMuxCtaFreeJoinCopyActive(funnel);
    const bannerEnv = funnel?.friendlyBanner ?? readMuxCtaFriendlyBannerEnv();
    if (bannerEnv === "off" && !freeJoin) return null;
    const lines = freeJoin ? MUX_FRIENDLY_BANNER_FREE : MUX_FRIENDLY_BANNER_SOFT;
    return lines[lang] || lines.en;
  }

  const svc = ctx.service;
  const staticPage = ctx.staticPage ?? null;
  const isTelegramLike =
    staticPage === "grupo-privado" ||
    svc === "telegram" ||
    svc === "telegram_groups" ||
    svc === "channel_invite";
  if (!isTelegramLike) return null;

  const freeJoin = isMuxCtaFreeJoinCopyActive(funnel);
  const bannerEnv = funnel?.friendlyBanner ?? readMuxCtaFriendlyBannerEnv();
  if (bannerEnv === "off" && !freeJoin) return null;

  if (!freeJoin && bannerEnv !== "on") return null;

  const lines = freeJoin ? MUX_FRIENDLY_BANNER_FREE : MUX_FRIENDLY_BANNER_SOFT;
  return lines[lang] || lines.en;
}

export interface VideoCTAConfig {
  playbackId: string;
  ctaUrl: string;
  ctaLabel?: Record<SupportedLanguage, string>;
  title?: Record<SupportedLanguage, string>;
  playbackIdByCountry?: Record<string, string>;
  ctaUrlByCountry?: Record<string, string>;
  /** Barrios CABA/GBA beta — dual MP tiers cuando el Worker resolvió mejores_amigos. */
  mejoresAmigosTiers?: Partial<
    Record<MejoresAmigosTierKey, { url: string; label: string }>
  >;
  neighborhoodBetaLabel?: string | null;
}

const MEJORES_AMIGOS_CTA_LABELS: Record<SupportedLanguage, string> = {
  es: "Mejores Amigos",
  en: "Close Friends",
  de: "Close Friends",
  fr: "Amis proches",
  it: "Migliori amici",
  pt: "Melhores amigos",
  nl: "Close Friends",
  tr: "Yakın Arkadaşlar",
  ar: "أفضل الأصدقاء",
  pl: "Najlepsi znajomi",
  ja: "親しい友達",
  ko: "친한 친구",
  id: "Teman Terdekat",
};

const MEJORES_AMIGOS_TITLE: Record<SupportedLanguage, string> = {
  es: "Mejores Amigos · Instagram",
  en: "Close Friends · Instagram",
  de: "Close Friends · Instagram",
  fr: "Amis proches · Instagram",
  it: "Migliori amici · Instagram",
  pt: "Melhores amigos · Instagram",
  nl: "Close Friends · Instagram",
  tr: "Yakın Arkadaşlar · Instagram",
  ar: "أفضل الأصدقاء · إنستغرام",
  pl: "Najlepsi znajomi · Instagram",
  ja: "親しい友達 · Instagram",
  ko: "친한 친구 · Instagram",
  id: "Teman Terdekat · Instagram",
};

/**
 * ¿El visitante está en beta de barrio con CTA Mejores Amigos activo?
 */
export function isNeighborhoodBetaMejoresAmigos(ctx: NeighborhoodVideoCtaContext): boolean {
  if (ctx.country && ctx.country !== "AR") return false;
  const active = !!(ctx.showBestFriends || ctx.showBirthday);
  if (!active) return false;
  const hasTier =
    Boolean(ctx.mejoresAmigosTiers?.medium?.url) ||
    Boolean(ctx.mejoresAmigosTiers?.high?.url) ||
    Boolean(ctx.ctaUrl?.trim());
  if (!hasTier) return false;
  return Boolean(ctx.neighborhood?.trim());
}

/**
 * Resuelve video + CTAs para barrio beta (CABA/GBA) desde flags del Worker.
 */
export function resolveNeighborhoodBetaVideoCta(
  ctx: NeighborhoodVideoCtaContext,
  language: SupportedLanguage = "es",
): VideoCTAConfig | null {
  if (!isNeighborhoodBetaMejoresAmigos(ctx)) return null;

  const country = ctx.country || "AR";
  const primaryUrl =
    ctx.ctaUrl?.trim() ||
    ctx.mejoresAmigosTiers?.medium?.url ||
    ctx.mejoresAmigosTiers?.high?.url ||
    getMejoresAmigosCTAUrlByCountry(country);

  const city = ctx.city || ctx.neighborhoodCity || null;
  const betaLabel = formatNeighborhoodBetaLabel(ctx.neighborhood, city);

  return {
    playbackId: getMejoresAmigosPlaybackIdByCountry(country) || MEJORES_AMIGOS_PLAYBACK_ID,
    ctaUrl: primaryUrl,
    ctaLabel: {
      ...MEJORES_AMIGOS_CTA_LABELS,
      es: ctx.ctaLabel?.trim() || MEJORES_AMIGOS_CTA_LABELS.es,
    },
    title: {
      ...MEJORES_AMIGOS_TITLE,
      es: betaLabel
        ? `${MEJORES_AMIGOS_TITLE.es} · ${betaLabel}`
        : MEJORES_AMIGOS_TITLE.es,
    },
    mejoresAmigosTiers: ctx.mejoresAmigosTiers,
    neighborhoodBetaLabel: betaLabel,
  };
}

/**
 * Get default video CTA configuration for a service
 * Solo channel_invite (telegram groups) - lo principal
 */
function getDefaultVideoConfig(service: VideoCTAService): VideoCTAConfig {
  const telegramConfig = getTelegramGroupsVideoConfig();

  switch (service) {
    case "telegram":
    case "telegram_groups":
    case "channel_invite": {
      const freeJoin = isMuxCtaFreeJoinCopyActive(null);
      return {
        playbackId: TELEGRAM_GROUPS_PLAYBACK_ID,
        ctaUrl: TELEGRAM_GROUPS_CTA_URL,
        playbackIdByCountry: telegramConfig.playbackIds,
        ctaUrlByCountry: telegramConfig.ctaUrls,
        ctaLabel: freeJoin
          ? {
              en: "Free Access",
              es: "Acceso Gratis",
              de: "Kostenloser Zugang",
              fr: "Accès Gratuit",
              it: "Accesso Gratuito",
              pt: "Acesso Grátis",
              nl: "Gratis Toegang",
              tr: "Ücretsiz Erişim",
              ar: "وصول مجاني",
              pl: "Darmowy Dostęp",
              ja: "無料アクセス",
              ko: "무료 접근",
              id: "Akses Gratis",
            }
          : {
              en: "Exclusive Member",
              es: "Miembro Exclusivo",
              de: "Exklusives Mitglied",
              fr: "Membre Exclusif",
              it: "Membro Esclusivo",
              pt: "Membro Exclusivo",
              nl: "Exclusief Lid",
              tr: "Özel Üye",
              ar: "عضو حصري",
              pl: "Ekskluzywny Członek",
              ja: "エクスクルーシブメンバー",
              ko: "익스클루시브 멤버",
              id: "Member Eksklusif",
            },
        title: freeJoin
          ? {
              en: "Free Community Access (not VIP)",
              es: "Acceso Gratis a la Comunidad (no es VIP)",
              de: "Kostenloser Community-Zugang (kein VIP)",
              fr: "Accès Communauté Gratuit (pas VIP)",
              it: "Accesso Gratuito alla Community (non VIP)",
              pt: "Acesso Grátis à Comunidade (não é VIP)",
              nl: "Gratis Community-toegang (geen VIP)",
              tr: "Ücretsiz Topluluk Erişimi (VIP değil)",
              ar: "وصول مجاني للمجتمع (ليس VIP)",
              pl: "Darmowy Dostęp do Społeczności (nie VIP)",
              ja: "無料コミュニティアクセス（VIPではありません）",
              ko: "무료 커뮤니티 접근 (VIP 아님)",
              id: "Akses Komunitas Gratis (bukan VIP)",
            }
          : {
              en: "Exclusive Community (not the VIP plan)",
              es: "Comunidad Exclusiva (no es el plan VIP)",
              de: "Exklusive Community (kein VIP-Plan)",
              fr: "Communauté Exclusive (pas le forfait VIP)",
              it: "Community Esclusiva (non il piano VIP)",
              pt: "Comunidade Exclusiva (não é o plano VIP)",
              nl: "Exclusieve Community (niet het VIP-plan)",
              tr: "Özel Topluluk (VIP planı değil)",
              ar: "المجتمع الحصري (ليست خطة VIP)",
              pl: "Ekskluzywna Społeczność (nie plan VIP)",
              ja: "エクスクルーシブコミュニティ（VIPプランではありません）",
              ko: "익스클루시브 커뮤니티 (VIP 플랜 아님)",
              id: "Komunitas Eksklusif (bukan paket VIP)",
            },
      };
    }
    case "mejores_amigos": {
      const country = "AR";
      return {
        playbackId: getMejoresAmigosPlaybackIdByCountry(country) || MEJORES_AMIGOS_PLAYBACK_ID,
        ctaUrl: getMejoresAmigosCTAUrlByCountry(country),
        ctaLabel: MEJORES_AMIGOS_CTA_LABELS,
        title: MEJORES_AMIGOS_TITLE,
      };
    }
    case "custom":
    default:
      return {
        playbackId: "",
        ctaUrl: "",
        ctaLabel: {
          en: "Exclusive Member",
          es: "Miembro Exclusivo",
          de: "Exklusives Mitglied",
          fr: "Membre Exclusif",
          it: "Membro Esclusivo",
          pt: "Membro Exclusivo",
          nl: "Exclusief Lid",
          tr: "Özel Üye",
          ar: "عضو حصري",
          pl: "Ekskluzywny Członek",
          ja: "エクスクルーシブメンバー",
          ko: "익스클루시브 멤버",
          id: "Member Eksklusif",
        },
      };
  }
}

export function getVideoCTAConfig(
  service: VideoCTAService,
  countryCode?: string,
  language: SupportedLanguage = "es",
  neighborhoodCtx?: NeighborhoodVideoCtaContext | null,
): VideoCTAConfig {
  if (neighborhoodCtx) {
    const beta = resolveNeighborhoodBetaVideoCta(
      { ...neighborhoodCtx, country: neighborhoodCtx.country || countryCode },
      language,
    );
    if (beta) return beta;
  }
  return getDefaultVideoConfig(service);
}

export function hasVideoConfigured(service: VideoCTAService): boolean {
  switch (service) {
    case "telegram":
    case "telegram_groups":
    case "channel_invite":
      return hasChannelInviteVideo();
    case "mejores_amigos":
      return Boolean(MEJORES_AMIGOS_PLAYBACK_ID || getMejoresAmigosPlaybackIdByCountry("AR"));
    case "custom":
    default:
      return false;
  }
}

export async function getVideoCTAConfigWithPayment(
  service: VideoCTAService,
  countryCode: string = "US",
  language: SupportedLanguage = "es",
  neighborhoodCtx?: NeighborhoodVideoCtaContext | null,
): Promise<VideoCTAConfig & { hasVideo: boolean }> {
  const config = getVideoCTAConfig(service, countryCode, language, neighborhoodCtx);
  const hasVideo = hasVideoConfigured(service);
  return { ...config, hasVideo };
}

export function getLocalizedLabel(
  config: VideoCTAConfig,
  language: SupportedLanguage = "es"
): string {
  if (config.ctaLabel) {
    return config.ctaLabel[language] || config.ctaLabel.en || "Join";
  }
  // Fallback translations
  const fallbacks: Record<SupportedLanguage, string> = {
    en: "Exclusive Member",
    es: "Miembro Exclusivo",
    de: "Exklusives Mitglied",
    fr: "Membre Exclusif",
    it: "Membro Esclusivo",
    pt: "Membro Exclusivo",
    nl: "Exclusief Lid",
    tr: "Özel Üye",
    ar: "عضو حصري",
    pl: "Ekskluzywny Członek",
    ja: "エクスクルーシブメンバー",
    ko: "익스클루시브 멤버",
    id: "Member Eksklusif",
  };
  return fallbacks[language] || fallbacks.en;
}

export function getLocalizedTitle(
  config: VideoCTAConfig,
  language: SupportedLanguage = "es"
): string | undefined {
  if (config.title) {
    return config.title[language] || config.title.en;
  }
  return undefined;
}

export default {
  getVideoCTAConfig,
  getVideoCTAConfigWithPayment,
  getLocalizedLabel,
  getLocalizedTitle,
  getDefaultVideoConfig,
};
